import { useState, useEffect, useCallback, type ReactNode } from 'react';
import { useSessionState } from '../hooks/useSessionState';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  Plus,
  FilterX,
  Edit,
  Trash2,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  ChevronDown,
  ChevronsUpDown,
  FileText,
  Trophy,
  Clock,
  Euro,
  AlertTriangle,
  Settings2,
  Check,
  X,
  ExternalLink,
  RefreshCw,
  Inbox,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { StatCardGrid } from '../components/StatCardGrid';
import { formatCurrency } from '../lib/utils';
import { api } from '../core/apiClient';
import { useCompositeCodes, compositeCodeLabel } from '../hooks/useCompositeCodes';
import { ExcelFilterDropdown, type ExcelFilterOption } from '../components/ui/excel-filter';

// ---- Mapa de dies de la setmana en català (sense toLocaleString) ----
const DIA_MAP: Record<number, string> = {
  0: 'Diumenge',
  1: 'Dilluns',
  2: 'Dimarts',
  3: 'Dimecres',
  4: 'Dijous',
  5: 'Divendres',
  6: 'Dissabte',
};

// ---- Color de fila per estat ----
const ROW_COLOR: Record<string, string> = {
  CANDIDATA: 'bg-sky-50',
  DESCARTADA: 'bg-slate-50 opacity-60',
  NO_PRESENTAT: 'bg-orange-50',
  PRESENTAT_PENDENT: 'bg-green-50',
  PRESENTAT_PERDUT: 'bg-slate-100',
  GUANYAT: 'bg-amber-50',
};

// ---- Labels i variant Badge per estat ----
const STATUS_CONFIG: Record<string, { label: string; variant: string }> = {
  CANDIDATA: { label: 'Candidat', variant: 'warning' },
  DESCARTADA: { label: 'Descartat', variant: 'outline' },
  NO_PRESENTAT: { label: 'En estudi', variant: 'secondary' },
  PRESENTAT_PENDENT: { label: 'Licitat', variant: 'default' },
  PRESENTAT_PERDUT: { label: 'Desestimat', variant: 'outline' },
  GUANYAT: { label: 'Proposat', variant: 'success' },
};

// Opcions del filtre d'Estat a l'estil Excel (ExcelFilterDropdown) — mateixos
// valors que abans al <select>, ara marcables individualment i en qualsevol
// combinació (el backend ja accepta una llista de status separada per comes).
const STATUS_FILTER_OPTIONS: ExcelFilterOption[] = [
  { value: 'CANDIDATA', label: 'Candidats (per revisar)' },
  { value: 'NO_PRESENTAT', label: 'En estudi' },
  { value: 'PRESENTAT_PENDENT', label: 'Licitat' },
  { value: 'PRESENTAT_PERDUT', label: 'Desestimat' },
  { value: 'GUANYAT', label: 'Adjudicat' },
  { value: 'DESCARTADA', label: 'Descartat' },
];


interface TenderConstructor {
  id: string;
  name: string;
}

interface Tender {
  id: string;
  noticeDate?: string;
  deadline?: string;
  location?: string;
  totalAmount?: string | number | null;
  adjudicationAmount?: string | number | null;
  tenderType?: 'PUBLIC' | 'PRIVAT';
  compositeCodeId?: number | null;
  observations?: string;
  result?: string;
  status: string;
  tenderConstructor?: TenderConstructor;
  source?: string | null;
  organisme?: string | null;
  sourceUrl?: string | null;
  placspUrl?: string | null;
  externalRef?: string | null;
  pliegosStatus?: string | null;
  affinity?: string | null;
  responsable?: string | null;
}

interface CompanyUser { id: string; firstName: string; lastName: string; }

interface IngestStatus {
  enabled: boolean;
  mailConfigured: boolean;
  running: boolean;
  lastMailRun: string | null;
  lastMailError: string | null;
  lastPlacspRun: string | null;
  lastPlacspError: string | null;
  lastMailAt: string | null;
  silentDays: number | null;
  candidates: number;
  pendingPliegos: number;
}

const PLIEGOS_LABEL: Record<string, string> = {
  ENLLAC: 'Plecs: pendents de descarregar',
  PENDENT: 'Plecs: cercant a PLACSP…',
  DESCARREGATS: 'Plecs descarregats',
  SENSE_PDF: 'Sense PDF a PLACSP',
  NO_TROBAT: 'No trobada a PLACSP',
};

interface Stats {
  total: number;
  guanyades: number;
  presentadesPendent: number;
  importGuanyat: number;
}

function formatDate(dateStr?: string): string {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '—';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yy = String(d.getFullYear()).slice(2);
  return `${dd}/${mm}/${yy}`;
}

function formatTime(dateStr?: string): string {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '—';
  const hh = String(d.getHours()).padStart(2, '0');
  const min = String(d.getMinutes()).padStart(2, '0');
  return `${hh}:${min}`;
}

function getDayName(dateStr?: string): string {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '—';
  return DIA_MAP[d.getDay()] ?? '—';
}

export default function Licitacions() {
  const navigate = useNavigate();
  const [tenders, setTenders] = useState<Tender[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [ingest, setIngest] = useState<IngestStatus | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [ingestRunning, setIngestRunning] = useState(false);
  const [stats, setStats] = useState<Stats>({ total: 0, guanyades: 0, presentadesPendent: 0, importGuanyat: 0 });

  // Filtres
  const [search, setSearch] = useSessionState('licitacions:search', '');
  // Per defecte nomes es mostren les licitacions actives (Pendent +
  // Presentat Pendent) — "Tots els estats" cal seleccionar-lo expressament.
  const [statusFilter, setStatusFilter] = useSessionState('licitacions:statusFilter', 'NO_PRESENTAT,PRESENTAT_PENDENT');
  const [compositeFilter, setCompositeFilter] = useSessionState('licitacions:compositeCodeFilter', 'all');
  const [compositeSubfamilyFilter, setCompositeSubfamilyFilter] = useSessionState('licitacions:compositeSubfamilyFilter', '');
  const [compositeSubfamilies, setCompositeSubfamilies] = useState<{ id: number; code: string; name: string; compositeCodeId: number; isActive: number | boolean }[]>([]);
  const [yearFilter, setYearFilter] = useSessionState('licitacions:yearFilter', 'all');
  const [responsableFilter, setResponsableFilter] = useSessionState('licitacions:responsableFilter', 'all');
  const [companyUsers, setCompanyUsers] = useState<CompanyUser[]>([]);
  const [statusFilterOptions, setStatusFilterOptions] = useState<ExcelFilterOption[]>(STATUS_FILTER_OPTIONS);

  const compositeCodes = useCompositeCodes();

  useEffect(() => {
    api.get<{ success: boolean; data: CompanyUser[] }>('/auth/users')
      .then((res) => setCompanyUsers(res.data ?? []))
      .catch(() => setCompanyUsers([]));
    // Opcions del filtre d'Estat — catàleg unificat admin/estats-crm
    // (2026-10-20), amb STATUS_FILTER_OPTIONS nomes com a valor per defecte
    // mentre carrega (el nom sempre ve del catàleg, editable a Admin >
    // Taules > CRM — mateix patró que ExpedientComercialList.tsx/estatLabel).
    api.get<{ success: boolean; data: { code: string; name: string; isActive: number }[] }>('/admin/estats-crm', { module: 'licitacions' })
      .then((res) => {
        const opts = (res.data ?? [])
          .filter((e) => e.isActive)
          .map((e) => ({ value: e.code, label: e.name }));
        if (opts.length > 0) setStatusFilterOptions(opts);
      })
      .catch(() => {});
    api.get<{ success: boolean; data: typeof compositeSubfamilies }>('/catalog/composite-subfamilies')
      .then((res) => setCompositeSubfamilies((res.data ?? []).filter((s) => Number(s.isActive) === 1)))
      .catch(() => setCompositeSubfamilies([]));
  }, []);

  // Ordenació
  const [sortBy, setSortBy] = useSessionState('licitacions:sortBy', 'deadline');
  const [sortOrder, setSortOrder] = useSessionState<'ASC' | 'DESC'>('licitacions:sortOrder', 'DESC');
  // Les candidates es revisen per ordre de venciment: de la més antiga a la més nova.
  const chooseStatus = (value: string) => {
    setStatusFilter(value);
    if (value === 'CANDIDATA') {
      setSortBy('deadline');
      setSortOrder('ASC');
    }
  };
  const toggleSort = (field: string) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC');
    } else {
      setSortBy(field);
      setSortOrder('ASC');
    }
  };

  // Paginació
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 25;

  // Anys disponibles (últims 5)
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 6 }, (_, i) => currentYear - i);

  const fetchTenders = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        sortBy,
        sortOrder,
      });
      if (search) params.append('search', search);
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (compositeFilter !== 'all') params.append('compositeCodeId', compositeFilter);
      if (compositeSubfamilyFilter) params.append('compositeSubfamilyId', compositeSubfamilyFilter);
      if (yearFilter !== 'all') params.append('year', yearFilter);
      if (responsableFilter !== 'all') params.append('responsable', responsableFilter);

      const [listRes, statsRes] = await Promise.all([
        api.get<{
          success: boolean;
          data: Tender[];
          pagination: { total: number; page: number; limit: number; totalPages: number };
        }>(`/tenders?${params}`),
        api.get<{ success: boolean; data: Stats }>('/tenders/stats'),
      ]);

      setTenders(listRes.data);
      setTotalPages(listRes.pagination.totalPages);
      setTotal(listRes.pagination.total);
      setStats(statsRes.data);
    } catch (err) {
      console.error('[v0] Error fetching tenders:', err);
      setError('Error en carregar les licitacions');
      setTenders([]);
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter, compositeFilter, compositeSubfamilyFilter, yearFilter, responsableFilter, page, sortBy, sortOrder]);

  const fetchIngest = useCallback(async (): Promise<IngestStatus | null> => {
    try {
      const res = await api.get<{ success: boolean; data: IngestStatus }>('/tenders/ingest/status');
      setIngest(res.data);
      return res.data;
    } catch {
      setIngest(null);
      return null;
    }
  }, []);

  useEffect(() => {
    fetchIngest();
  }, [fetchIngest]);

  // Amb candidates noves el procés (fitxa + PLACSP + plecs + informe per cadascuna) pot trigar uns
  // quants minuts: en lloc d'un únic refresc a un temps fix, es pregunta l'estat cada 5s fins que el
  // servidor digui que ha acabat (running=false), amb un límit de seguretat de 10 minuts.
  const POLL_INTERVAL_MS = 5000;
  const POLL_MAX_ATTEMPTS = 120;
  const pollIngestUntilDone = async (attempt = 0) => {
    const status = await fetchIngest();
    if (status?.running && attempt < POLL_MAX_ATTEMPTS) {
      setTimeout(() => pollIngestUntilDone(attempt + 1), POLL_INTERVAL_MS);
      return;
    }
    setIngestRunning(false);
    fetchTenders();
  };

  const runIngestNow = async () => {
    setIngestRunning(true);
    try {
      await api.post('/tenders/ingest/run', {});
      // S'executa en segon pla: es sondeja l'estat fins que el servidor confirmi que ha acabat.
      setTimeout(() => pollIngestUntilDone(), 2000);
    } catch (err: any) {
      alert(err?.response?.data?.message || "No s'ha pogut iniciar la cerca");
      setIngestRunning(false);
    }
  };

  const setTenderStatus = async (tender: Tender, status: 'NO_PRESENTAT' | 'DESCARTADA') => {
    try {
      await api.put(`/tenders/${tender.id}`, { status });
      fetchTenders();
      fetchIngest();
    } catch (err) {
      console.error('Error canviant estat de la licitació:', err);
      alert("Error en canviar l'estat de la licitació");
    }
  };

  useEffect(() => {
    fetchTenders();
  }, [fetchTenders]);

  useEffect(() => {
    setPage(1);
  }, [search, statusFilter, compositeFilter, compositeSubfamilyFilter, yearFilter, responsableFilter, sortBy, sortOrder]);

  const SortIcon = ({ field }: { field: string }) => {
    if (sortBy !== field) return <ChevronsUpDown className="h-3 w-3 text-muted-foreground/50" />;
    return sortOrder === 'ASC' ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />;
  };
  const SortHead = ({ field, className, align = 'left', children }: { field: string; className?: string; align?: 'left' | 'right'; children: ReactNode }) => (
    <TableHead className={className}>
      <button
        type="button"
        onClick={() => toggleSort(field)}
        className={`flex items-center gap-1 hover:text-foreground w-full ${align === 'right' ? 'justify-end' : ''}`}
      >
        {children} <SortIcon field={field} />
      </button>
    </TableHead>
  );

  const handleDelete = async (tender: Tender) => {
    if (!window.confirm(`Segur que vols eliminar aquesta licitació?`)) return;
    try {
      await api.delete(`/tenders/${tender.id}`);
      fetchTenders();
    } catch (err) {
      console.error('[v0] Error deleting tender:', err);
      alert('Error en eliminar la licitació');
    }
  };

  // ---- Eliminació definitiva en bloc de Descartades (amb els documents adjunts) ----
  const isDescartadaView = statusFilter === 'DESCARTADA';
  const allOnPageSelected = tenders.length > 0 && tenders.every((t) => selectedIds.has(t.id));

  useEffect(() => {
    setSelectedIds(new Set());
  }, [statusFilter]);

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAllOnPage = () => {
    setSelectedIds((prev) => {
      if (tenders.every((t) => prev.has(t.id))) {
        const next = new Set(prev);
        tenders.forEach((t) => next.delete(t.id));
        return next;
      }
      const next = new Set(prev);
      tenders.forEach((t) => next.add(t.id));
      return next;
    });
  };

  const purgeDescartades = async (ids?: string[]) => {
    const count = ids ? ids.length : total;
    if (count === 0) return;
    const warning = ids
      ? `Segur que vols eliminar definitivament ${count} licitació${count === 1 ? '' : 's'} descartada${count === 1 ? '' : 'es'}? També s'eliminaran els seus documents adjunts. Aquesta acció NO es pot desfer.`
      : `Segur que vols eliminar definitivament TOTES les ${count} licitacions descartades? També s'eliminaran els seus documents adjunts. Aquesta acció NO es pot desfer.`;
    if (!window.confirm(warning)) return;
    try {
      const res = await api.delete<{ success: boolean; message?: string }>('/tenders/descartades/purge', { ids });
      setSelectedIds(new Set());
      fetchTenders();
      fetchIngest();
      if (res.message) alert(res.message);
    } catch (err: any) {
      alert(err?.response?.data?.message || "No s'han pogut eliminar les licitacions");
    }
  };

  return (
    <div className="space-y-4">
      {/* Ingesta automàtica (Infoconcurso + PLACSP) */}
      {ingest?.enabled && (
        <Card className="rounded-xl">
          <CardContent className="pt-4">
            <div className="flex flex-col md:flex-row md:items-center gap-3">
              <Inbox className="h-5 w-5 text-sky-600 shrink-0" />
              <div className="flex-1 text-sm">
                <p className="font-medium">
                  {ingest.candidates > 0
                    ? `${ingest.candidates} licitació${ingest.candidates === 1 ? '' : 's'} candidata${ingest.candidates === 1 ? '' : 's'} per revisar`
                    : 'Cap licitació candidata per revisar'}
                  {ingest.pendingPliegos > 0 && (
                    <span className="text-muted-foreground font-normal"> · {ingest.pendingPliegos} cercant plecs a PLACSP</span>
                  )}
                </p>
                <p className="text-xs text-muted-foreground">
                  Darrer avís d&apos;Infoconcurso: {ingest.lastMailAt ? new Date(ingest.lastMailAt).toLocaleDateString('ca-ES') : 'cap encara'}
                  {ingest.lastMailRun && ` · darrera lectura del correu: ${new Date(ingest.lastMailRun).toLocaleString('ca-ES')}`}
                </p>
                {ingest.silentDays != null && ingest.silentDays >= 4 && (
                  <p className="text-xs text-amber-700 mt-1 flex items-center gap-1">
                    <AlertTriangle className="h-3.5 w-3.5" />
                    Fa {ingest.silentDays} dies que no arriba cap avís d&apos;Infoconcurso. Comprova que la subscripció no hagi caducat.
                  </p>
                )}
                {(ingest.lastMailError || ingest.lastPlacspError) && (
                  <p className="text-xs text-red-600 mt-1">{ingest.lastMailError || ingest.lastPlacspError}</p>
                )}
              </div>
              <div className="flex items-center gap-2">
                {ingest.candidates > 0 && (
                  <button
                    type="button"
                    onClick={() => chooseStatus('CANDIDATA')}
                    className="px-3 py-2 text-sm border rounded-lg hover:bg-muted transition-colors"
                  >
                    Veure candidates
                  </button>
                )}
                <button
                  type="button"
                  onClick={runIngestNow}
                  disabled={ingestRunning || ingest.running || !ingest.mailConfigured}
                  className="flex items-center gap-2 px-3 py-2 text-sm border rounded-lg hover:bg-muted transition-colors disabled:opacity-50"
                >
                  <RefreshCw className={`h-4 w-4 ${ingestRunning || ingest.running ? 'animate-spin' : ''}`} />
                  Buscar ara
                </button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filtres */}
      <Card className="rounded-xl">
        <CardContent className="pt-4">
          <div className="flex flex-col md:flex-row gap-3 flex-wrap">
            <button
              type="button"
              onClick={() => { setSearch(''); setStatusFilter('all'); setCompositeFilter('all'); setCompositeSubfamilyFilter(''); setYearFilter('all'); }}
              title="Netejar tots els filtres"
              className="flex items-center justify-center px-2.5 border rounded-lg hover:bg-muted transition-colors shrink-0"
            >
              <FilterX className="h-4 w-4 text-muted-foreground" />
            </button>
            <div className="flex-1 relative min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <input
                autoFocus
                type="text"
                placeholder="Cercar per població o observacions..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <div className="flex items-center gap-2">
              <ExcelFilterDropdown
                label="Estat"
                options={statusFilterOptions}
                selected={statusFilter === 'all' ? [] : statusFilter.split(',')}
                onApply={(values) => chooseStatus(values.length === 0 ? 'all' : values.join(','))}
              />
            </div>
            <select
              value={compositeFilter}
              onChange={(e) => { setCompositeFilter(e.target.value); setCompositeSubfamilyFilter(''); }}
              className="px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-background"
            >
              <option value="all">Tots els tipus de producte</option>
              {compositeCodes.optionsFor(compositeFilter === 'all' ? null : compositeFilter).map((cc) => (
                <option key={cc.id} value={String(cc.id)}>{compositeCodeLabel(cc)}</option>
              ))}
            </select>
            <ExcelFilterDropdown
              label="Subfamília"
              options={compositeSubfamilies
                .filter((s) => compositeFilter === 'all' || String(s.compositeCodeId) === compositeFilter)
                .map((s) => ({ value: String(s.id), label: s.name }))}
              selected={compositeSubfamilyFilter ? compositeSubfamilyFilter.split(',') : []}
              onApply={(values) => setCompositeSubfamilyFilter(values.join(','))}
              buttonClassName="px-3 py-2 text-sm"
            />
            <select
              value={yearFilter}
              onChange={(e) => setYearFilter(e.target.value)}
              className="px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-background"
            >
              <option value="all">Tots els anys</option>
              {years.map((y) => (
                <option key={y} value={y.toString()}>{y}</option>
              ))}
            </select>
            <select
              value={responsableFilter}
              onChange={(e) => setResponsableFilter(e.target.value)}
              className="px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-background"
            >
              <option value="all">Tots els responsables</option>
              {companyUsers.map((u) => (
                <option key={u.id} value={`${u.firstName} ${u.lastName}`}>{u.firstName} {u.lastName}</option>
              ))}
            </select>
          </div>
        </CardContent>
      </Card>

      {/* Taula */}
      <Card className="rounded-xl">
        <CardHeader className="pb-3 border-b">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
              Licitacions
            </CardTitle>
            <div className="flex items-center gap-3">
              {isDescartadaView && total > 0 && (
                <div className="flex items-center gap-2">
                  {selectedIds.size > 0 && (
                    <button
                      type="button"
                      onClick={() => purgeDescartades([...selectedIds])}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-red-50 text-red-700 rounded-lg hover:bg-red-100 transition-colors"
                    >
                      <Trash2 className="h-3.5 w-3.5" /> Eliminar seleccionades ({selectedIds.size})
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => purgeDescartades(undefined)}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-red-200 text-red-700 rounded-lg hover:bg-red-50 transition-colors"
                  >
                    <Trash2 className="h-3.5 w-3.5" /> Eliminar totes les descartades ({total})
                  </button>
                </div>
              )}
              <span className="text-sm text-muted-foreground">{total} licitacions trobades</span>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {error ? (
            <div className="flex flex-col items-center justify-center py-12">
              <AlertTriangle className="h-12 w-12 text-destructive mb-4" />
              <p className="text-sm font-medium">{error}</p>
              <button onClick={fetchTenders} className="mt-4 px-4 py-2 bg-primary text-primary-foreground rounded-lg">
                Reintentar
              </button>
            </div>
          ) : loading ? (
            <div className="space-y-3 p-6">
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className="h-14 w-full" />
              ))}
            </div>
          ) : tenders.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12">
              <FileText className="h-12 w-12 text-muted-foreground mb-4" />
              <p className="text-sm font-medium text-muted-foreground">No s&apos;han trobat licitacions</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table className="table-fixed">
                  <TableHeader>
                    <TableRow>
                      {isDescartadaView && (
                        <TableHead className="w-[36px] px-2">
                          <input
                            type="checkbox"
                            checked={allOnPageSelected}
                            onChange={toggleSelectAllOnPage}
                            title="Seleccionar totes les d'aquesta pàgina"
                          />
                        </TableHead>
                      )}
                      <SortHead field="deadline" className="w-[84px]">Venciment</SortHead>
                      <TableHead className="w-[44px]">Hora</TableHead>
                      <TableHead className="w-[56px]">Dia</TableHead>
                      <SortHead field="location" className="w-[280px]">Objecte</SortHead>
                      <SortHead field="compositeCode" className="w-[120px]">Tipus de producte</SortHead>
                      <SortHead field="totalAmount" className="w-[84px]" align="right">Import Total</SortHead>
                      <SortHead field="adjudicationAmount" className="w-[84px]" align="right">Import Adjudicació</SortHead>
                      <TableHead className="w-[60px] text-right">Ràtio</TableHead>
                      <SortHead field="responsable" className="w-[110px]">Responsable</SortHead>
                      <SortHead field="status" className="w-[76px]">Estat</SortHead>
                      <TableHead className="w-[150px] px-2 text-right">Accions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {tenders.map((tender) => {
                      const statusCfg = {
                        label: statusFilterOptions.find((o) => o.value === tender.status)?.label ?? STATUS_CONFIG[tender.status]?.label ?? tender.status,
                        variant: STATUS_CONFIG[tender.status]?.variant ?? 'secondary',
                      };
                      const rowColor = ROW_COLOR[tender.status] ?? '';
                      const sanitizedAmount = Number(tender.totalAmount);
                      const sanitizedAdjudication = Number(tender.adjudicationAmount);
                      const hasAdjudication = !isNaN(sanitizedAdjudication) && tender.adjudicationAmount != null;
                      const ratio = hasAdjudication && !isNaN(sanitizedAmount) && sanitizedAmount > 0
                        ? (sanitizedAdjudication / sanitizedAmount) * 100
                        : null;

                      return (
                        <TableRow key={tender.id} className={rowColor}>
                          {isDescartadaView && (
                            <TableCell className="px-2" onClick={(e) => e.stopPropagation()}>
                              <input
                                type="checkbox"
                                checked={selectedIds.has(tender.id)}
                                onChange={() => toggleSelect(tender.id)}
                              />
                            </TableCell>
                          )}
                          <TableCell className="text-sm whitespace-nowrap">
                            {formatDate(tender.deadline)}
                          </TableCell>
                          <TableCell className="text-sm whitespace-nowrap">
                            {formatTime(tender.deadline)}
                          </TableCell>
                          <TableCell className="text-sm whitespace-nowrap">
                            {getDayName(tender.deadline)}
                          </TableCell>
                          <TableCell className="text-sm whitespace-normal break-words">
                            {tender.location || '—'}
                            {tender.source === 'INFOCONCURSO' && (
                              <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
                                {tender.organisme && <span>{tender.organisme}</span>}
                                {tender.pliegosStatus && <span>{PLIEGOS_LABEL[tender.pliegosStatus] ?? tender.pliegosStatus}</span>}
                                {tender.sourceUrl && (
                                  <a href={tender.sourceUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-0.5 text-blue-600 hover:underline">
                                    Infoconcurso <ExternalLink className="h-3 w-3" />
                                  </a>
                                )}
                                {tender.placspUrl && (
                                  <a href={tender.placspUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-0.5 text-blue-600 hover:underline">
                                    PLACSP <ExternalLink className="h-3 w-3" />
                                  </a>
                                )}
                              </div>
                            )}
                          </TableCell>
                          <TableCell className="text-sm whitespace-normal break-words">
                            {compositeCodes.nameOf(tender.compositeCodeId) || '—'}
                          </TableCell>
                          <TableCell className="text-right font-mono text-sm whitespace-nowrap">
                            {!isNaN(sanitizedAmount) && tender.totalAmount != null
                              ? formatCurrency(sanitizedAmount)
                              : '—'}
                          </TableCell>
                          <TableCell className="text-right font-mono text-sm whitespace-nowrap">
                            {hasAdjudication ? formatCurrency(sanitizedAdjudication) : '—'}
                          </TableCell>
                          <TableCell className="text-right font-mono text-sm whitespace-nowrap">
                            {ratio != null ? `${ratio.toLocaleString('ca-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} %` : '—'}
                          </TableCell>
                          <TableCell className="text-sm whitespace-nowrap">
                            {tender.responsable || '—'}
                          </TableCell>
                          <TableCell>
                            <Badge variant={statusCfg.variant as any}>
                              {statusCfg.label}
                            </Badge>
                          </TableCell>
                          <TableCell className="px-2">
                            <div className="flex items-center justify-end gap-0.5 whitespace-nowrap">
                              {tender.status === 'CANDIDATA' && (
                                <>
                                  <button
                                    onClick={() => setTenderStatus(tender, 'NO_PRESENTAT')}
                                    className="p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-green-600 transition-colors"
                                    title="Interessa: passa a En estudi"
                                  >
                                    <Check size={14} />
                                  </button>
                                  <button
                                    onClick={() => setTenderStatus(tender, 'DESCARTADA')}
                                    className="p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-red-600 transition-colors"
                                    title="Descartar"
                                  >
                                    <X size={14} />
                                  </button>
                                </>
                              )}
                              <button
                                onClick={() => navigate(`/crm/licitacions/${tender.id}/edit`)}
                                className="p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-blue-600 transition-colors"
                                title="Editar"
                              >
                                <Edit size={14} />
                              </button>
                              <button
                                onClick={() => handleDelete(tender)}
                                className="p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-red-600 transition-colors"
                                title="Eliminar"
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>

              {/* Paginació */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between px-6 py-4 border-t">
                  <p className="text-sm text-muted-foreground">
                    Pàgina {page} de {totalPages}
                  </p>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page === 1}
                      className="p-2 hover:bg-muted rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                      className="p-2 hover:bg-muted rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
